/**
 * Order Details
 * Tabbed detail view (Overview, Line Items, Timeline, History, Events) with delivery
 * figures, amendments and status updates.
 * Brand-teal industrial aesthetic.
 *
 * TypeScript since ST-00227 (was index.js). Audit batch ORD:
 *   ORD-2  Amend sends only the changed fields (`order_priority`, a numeric `order_size`)
 *          and shows the server's answer; the window stays open on failure.
 *   ORD-3  Delivered / Remaining / Completion come from the weighbridge tickets that carry
 *          the order number (`order.fulfilment`), against the order's (amended) size —
 *          only when the server tracks them (`fulfilment_tracking: 'tickets'`,
 *          ORDER_FULFILMENT_FROM_TICKETS). Off (the default) the page shows the order's own
 *          figures and says delivery tracking starts when on-site equipment is installed.
 *   ORD-12 Write controls only for roles that may manage orders.
 *   ORD-14 The status window offers the steps the server accepts; failures are shown.
 *   ORD-16 The fake "Base Layer" sync badge and visualiser are gone.
 *   ORD-17 Line Items: Add Item / Edit / Delete work.
 *   ORD-19 Live refresh matches the `order_no` the server sends.
 */

import { useState, useEffect, useCallback, type ComponentType, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit2,
  RefreshCw,
  XCircle,
  Plus,
  Trash,
  Clock,
  FileText,
  Activity,
  AlertCircle,
  Loader,
} from 'lucide-react';
import orderService from '../../../../services/orderService';
import useOrderSubscription from '../../../../hooks/useOrderSubscription';
import { StatusBadge, PriorityBadge, Skeleton, EmptyState, InlineAlert } from '../shared';
import ThemedSelect from '../../../common/ThemedSelect';
import { useAuth } from '../../../../types/commonComponents';
import { formatAppDateTime, formatMiningDate } from '../../../../utils/dateUtils';
import {
  allowedTransitions,
  apiErrorMessage,
  buildAmendment,
  formatTonnes,
  isOrderOpen,
  ORDERS_MANAGE,
} from '../shared/orderHelpers';
import type {
  OrderAmendmentData,
  OrderDetail,
  OrderEvent,
  OrderFulfilment,
  OrderHistoryEntry,
  OrderLineItem,
  OrderSocketPayload,
  ProductRow,
} from '../../../../types/clientPages/orderManagement';

const MONO = { fontFamily: 'IBM Plex Mono, monospace' };
const SANS = { fontFamily: 'DM Sans, sans-serif' };

const LABEL = 'block text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5';
const INPUT =
  'w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm';
const SELECT =
  'w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm bg-surface';

type TabId = 'overview' | 'lineItems' | 'timeline' | 'history' | 'events';

/* ─── Delivery figures (ORD-3) ────────────── */
const Metric = ({
  testId,
  label,
  children,
}: {
  testId: string;
  label: string;
  children: ReactNode;
}) => (
  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-testid={testId}>
    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1">
      {label}
    </p>
    {children}
  </div>
);

const Tonnes = ({ value }: { value: number | null | undefined }) => (
  <p className="text-2xl font-bold text-slate-900 tabular-nums" style={MONO}>
    {formatTonnes(value)} <span className="text-sm text-slate-400 font-medium">MT</span>
  </p>
);

const NotTracked = ({ text }: { text: string }) => (
  <p className="text-sm font-semibold text-slate-400 py-1.5">{text}</p>
);

const loads = (n: number) => `${n} ${n === 1 ? 'load' : 'loads'}`;

/** The sentence that says which tickets the figures come from. */
const FulfilmentBasis = ({ order, f }: { order: OrderDetail; f: OrderFulfilment }) => {
  const route =
    f.route_matched && order.dispatch_location_name && order.receipt_location_name
      ? `${order.dispatch_location_name} → ${order.receipt_location_name}`
      : null;
  const ticketCount = f.dispatched_loads + f.received_loads;
  const side = f.basis === 'Dispatch' ? 'dispatch' : 'receipt';
  return (
    <div
      className="mt-4 p-3 bg-slate-50 rounded-xl border border-slate-100 text-xs text-slate-600 space-y-1"
      data-testid="fulfilment-basis"
    >
      <p>
        {f.basis ? (
          <>
            Delivered counts the <strong>{side} tickets</strong> that carry order number{' '}
          </>
        ) : (
          <>Weighbridge tickets that carry order number </>
        )}
        <span className="font-semibold" style={MONO}>
          {order.order_no}
        </span>
        {route ? <> on {route}</> : null}.
        {ticketCount === 0 && ' No tickets carry this order number yet.'}
      </p>
      {ticketCount > 0 && (
        <p className="tabular-nums" style={MONO}>
          Dispatched {formatTonnes(f.dispatched_tonnes)} MT ({loads(f.dispatched_loads)}) · Received{' '}
          {formatTonnes(f.received_tonnes)} MT ({loads(f.received_loads)})
          {f.first_ticket_at && f.last_ticket_at && (
            <>
              {' '}
              · {formatMiningDate(f.first_ticket_at)} – {formatMiningDate(f.last_ticket_at)}
            </>
          )}
        </p>
      )}
    </div>
  );
};

/** Shown where the delivery figures would be while tracking is off (ORD-3). */
const TRACKING_OFF_TEXT = 'Delivery tracking starts when on-site equipment is installed';

const DeliveryMetrics = ({ order }: { order: OrderDetail }) => {
  // An older server sends no fulfilment_tracking: it always tracked from tickets.
  if (order.fulfilment_tracking === 'off') {
    return (
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        data-testid="order-metrics"
      >
        <Metric testId="metric-size" label="Order Size">
          <Tonnes value={order.order_size} />
        </Metric>
        <div
          className="sm:col-span-1 lg:col-span-3 flex items-center px-4 py-3 bg-slate-50 rounded-xl border border-slate-100"
          data-testid="delivery-tracking-off"
        >
          <p className="text-sm text-slate-500">{TRACKING_OFF_TEXT}.</p>
        </div>
      </div>
    );
  }

  const f = order.fulfilment ?? null;
  const tracked = f !== null && f.basis !== null;
  const pct = tracked ? (f.completion_pct ?? 0) : null;
  const over = tracked ? (f.over_delivered_tonnes ?? 0) : 0;
  const notTrackedText = f ? 'Not tracked for this order type' : 'Delivery figures unavailable';

  return (
    <>
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        data-testid="order-metrics"
      >
        <Metric testId="metric-size" label="Order Size">
          <Tonnes value={order.order_size} />
        </Metric>
        <Metric testId="metric-delivered" label="Delivered">
          {tracked ? <Tonnes value={f.delivered_tonnes} /> : <NotTracked text={notTrackedText} />}
        </Metric>
        <Metric testId="metric-remaining" label="Remaining">
          {tracked ? (
            <>
              <Tonnes value={f.remaining_tonnes} />
              {over > 0 && (
                <p className="text-xs font-semibold text-amber-600 mt-0.5">
                  Over by {formatTonnes(over)} MT
                </p>
              )}
            </>
          ) : (
            <NotTracked text={notTrackedText} />
          )}
        </Metric>
        <Metric testId="metric-completion" label="Completion">
          {pct !== null ? (
            <div className="flex items-center gap-3">
              <p className="text-2xl font-bold text-slate-900 tabular-nums" style={MONO}>
                {pct.toLocaleString(undefined, { maximumFractionDigits: 1 })}%
              </p>
              <div className="flex-1 bg-slate-200 rounded-full h-2">
                <div
                  data-testid="completion-bar"
                  className="bg-gradient-to-r from-primary-500 to-emerald-400 h-2 rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                />
              </div>
            </div>
          ) : (
            <NotTracked text={notTrackedText} />
          )}
        </Metric>
      </div>
      {f && <FulfilmentBasis order={order} f={f} />}
    </>
  );
};

/* ─── Order Header ────────────────────────── */
interface OrderHeaderProps {
  order: OrderDetail;
  canManage: boolean;
  onStatusUpdate: () => void;
  onCreateAmendment: () => void;
  onCancel: () => void;
}

const OrderHeader = ({
  order,
  canManage,
  onStatusUpdate,
  onCreateAmendment,
  onCancel,
}: OrderHeaderProps) => (
  <div className="bg-surface rounded-xl border border-slate-200 p-6 mb-5">
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={MONO}>
          {order.order_no}
        </h1>
        <div className="flex items-center gap-3">
          <StatusBadge status={order.order_status} size="lg" />
          <PriorityBadge priority={order.order_priority} />
        </div>
      </div>
      {canManage && isOrderOpen(order.order_status) && (
        <div className="flex items-center gap-2">
          <button
            onClick={onStatusUpdate}
            className="px-3.5 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center text-xs font-semibold"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Update Status
          </button>
          <button
            onClick={onCreateAmendment}
            className="px-3.5 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors flex items-center text-xs font-semibold"
          >
            <Edit2 className="w-3.5 h-3.5 mr-1.5" /> Amend
          </button>
          <button
            onClick={onCancel}
            className="px-3.5 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center text-xs font-semibold"
          >
            <XCircle className="w-3.5 h-3.5 mr-1.5" /> Cancel
          </button>
        </div>
      )}
    </div>

    <DeliveryMetrics order={order} />
  </div>
);

/* ─── Tab Navigation ──────────────────────── */
const TABS: { id: TabId; label: string; icon: ComponentType<{ className?: string }> }[] = [
  { id: 'overview', label: 'Overview', icon: FileText },
  { id: 'lineItems', label: 'Line Items', icon: Activity },
  { id: 'timeline', label: 'Timeline', icon: Clock },
  { id: 'history', label: 'History', icon: RefreshCw },
  { id: 'events', label: 'Events', icon: AlertCircle },
];

const TabNavigation = ({
  activeTab,
  onTabChange,
}: {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
}) => (
  <div className="bg-surface rounded-xl border border-slate-200 mb-5 overflow-x-auto">
    <div className="flex">
      {TABS.map((tab) => {
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`flex items-center px-5 py-3.5 font-semibold transition-colors border-b-2 whitespace-nowrap text-sm ${
              activeTab === tab.id
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <Icon className="w-4 h-4 mr-2" />
            {tab.label}
          </button>
        );
      })}
    </div>
  </div>
);

/* ─── Overview Tab ────────────────────────── */
interface OverviewField {
  label: string;
  value: string | null | undefined;
}

const OverviewTab = ({ order }: { order: OrderDetail }) => {
  const sections: { title: string; fields: OverviewField[] }[] = [
    {
      title: 'Order Information',
      fields: [
        { label: 'Type', value: order.order_type },
        { label: 'Priority', value: order.order_priority },
        {
          label: 'Created',
          value: order.creation_date_time ? formatAppDateTime(order.creation_date_time) : 'N/A',
        },
        ...(order.last_modified_date_time
          ? [{ label: 'Last Updated', value: formatAppDateTime(order.last_modified_date_time) }]
          : []),
      ],
    },
    {
      title: 'Locations',
      fields: [
        { label: 'Dispatch', value: order.dispatch_location_name },
        { label: 'Receipt', value: order.receipt_location_name },
      ],
    },
  ];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {sections.map((section) => (
        <div key={section.title} className="bg-surface rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-bold text-slate-800 mb-4" style={SANS}>
            {section.title}
          </h3>
          <div className="space-y-3 text-sm">
            {section.fields.map((f) => (
              <div key={f.label}>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  {f.label}
                </span>
                <p className="font-medium text-slate-800">{f.value || 'N/A'}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
      {order.notes && (
        <div className="bg-surface rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-bold text-slate-800 mb-3" style={SANS}>
            Notes
          </h3>
          <p className="text-sm text-slate-600">{order.notes}</p>
        </div>
      )}
    </div>
  );
};

/* ─── Modal shell ─────────────────────────── */
const Modal = ({
  title,
  wide = false,
  children,
}: {
  title: string;
  wide?: boolean;
  children: ReactNode;
}) => {
  const titleId = `order-modal-${title.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`bg-surface rounded-xl shadow-2xl ${wide ? 'max-w-2xl' : 'max-w-md'} w-full p-6 max-h-[90vh] overflow-y-auto`}
      >
        <h3 id={titleId} className="text-lg font-bold text-slate-900 mb-5" style={SANS}>
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
};

const ModalFooter = ({
  submitLabel,
  submitting,
  disabled,
  onSubmit,
  onCancel,
}: {
  submitLabel: string;
  submitting: boolean;
  disabled?: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}) => (
  <div className="flex justify-end gap-2 mt-6">
    <button
      type="button"
      onClick={onCancel}
      className="px-4 py-2 text-slate-600 font-semibold text-sm hover:text-slate-800 transition-colors"
    >
      Cancel
    </button>
    <button
      type="button"
      onClick={onSubmit}
      disabled={disabled || submitting}
      className="px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm font-semibold flex items-center gap-2"
    >
      {submitting && <Loader className="w-4 h-4 animate-spin" aria-hidden="true" />}
      {submitLabel}
    </button>
  </div>
);

/* ─── Line Items Tab (ORD-17) ─────────────── */
interface LineItemsTabProps {
  lineItems: OrderLineItem[];
  canEdit: boolean;
  onRefresh: () => void;
  onAdd: () => void;
  onEdit: (item: OrderLineItem) => void;
  onDelete: (item: OrderLineItem) => void;
}

const LineItemsTab = ({
  lineItems,
  canEdit,
  onRefresh,
  onAdd,
  onEdit,
  onDelete,
}: LineItemsTabProps) => (
  <div className="bg-surface rounded-xl border border-slate-200">
    <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
      <h3 className="text-sm font-bold text-slate-800" style={SANS}>
        Line Items ({lineItems.length})
      </h3>
      <div className="flex items-center gap-2">
        <button
          onClick={onRefresh}
          className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Reload line items"
          title="Reload line items"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
        {canEdit && (
          <button
            onClick={onAdd}
            className="px-3 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center text-xs font-semibold"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Item
          </button>
        )}
      </div>
    </div>
    {lineItems.length === 0 ? (
      <EmptyState
        icon={Activity}
        title="No line items"
        description={
          canEdit ? 'Add products to this order with Add Item' : 'This order has no products listed'
        }
      />
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50/80">
              {['Product Code', 'Product Name', 'Quantity', 'Notes', ''].map((h) => (
                <th
                  key={h || 'actions'}
                  className={`px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-widest ${h === '' ? 'text-right' : 'text-left'}`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lineItems.map((item) => (
              <tr key={item.line_item_id} className="hover:bg-slate-50 transition-colors">
                <td className="px-5 py-3.5">
                  <span className="text-sm font-bold text-slate-900" style={MONO}>
                    {item.product_code}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-sm text-slate-700">{item.product_name}</td>
                <td className="px-5 py-3.5">
                  <span className="text-sm text-slate-800 tabular-nums" style={MONO}>
                    {Number(item.line_quantity).toLocaleString()}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-xs text-slate-500">{item.line_notes || '—'}</td>
                <td className="px-5 py-3.5 text-right">
                  {canEdit && (
                    <>
                      <button
                        onClick={() => onEdit(item)}
                        className="p-1.5 rounded text-primary-600 hover:bg-primary-50 mr-1 transition-colors"
                        aria-label={`Edit ${item.product_code}`}
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete(item)}
                        className="p-1.5 rounded text-red-500 hover:bg-red-50 transition-colors"
                        aria-label={`Delete ${item.product_code}`}
                        title="Delete"
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

/* ─── Line Item Modal (ORD-17) ────────────── */
interface LineItemInput {
  product_id: number;
  quantity: number;
  notes: string | null;
}

interface LineItemModalProps {
  /** null = Add, an item = Edit */
  item: OrderLineItem | null;
  products: ProductRow[];
  onSave: (data: LineItemInput) => Promise<void>;
  onCancel: () => void;
}

const LineItemModal = ({ item, products, onSave, onCancel }: LineItemModalProps) => {
  const [productId, setProductId] = useState<string>(item ? String(item.product_id) : '');
  const [quantity, setQuantity] = useState<string>(item ? String(item.line_quantity) : '');
  const [notes, setNotes] = useState<string>(item?.line_notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!productId) {
      setError('Choose a product.');
      return;
    }
    const qty = Number(quantity);
    if (quantity.trim() === '' || !Number.isFinite(qty) || qty <= 0) {
      setError('Quantity must be a number greater than 0.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onSave({ product_id: Number(productId), quantity: qty, notes: notes.trim() || null });
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to save the line item'));
      setSaving(false);
    }
  };

  return (
    <Modal title={item ? 'Edit Line Item' : 'Add Line Item'}>
      <div className="space-y-4">
        <div>
          <span className={LABEL}>
            Product <span className="text-red-400">*</span>
          </span>
          {item ? (
            <p className="text-sm font-semibold text-slate-800">
              {item.product_code} — {item.product_name}
            </p>
          ) : (
            <ThemedSelect
              value={productId}
              onChange={(e) => setProductId(String(e.target.value))}
              aria-label="Product"
              className={SELECT}
            >
              <option value="">Select Product</option>
              {products.map((p) => (
                <option key={p.product_id} value={String(p.product_id)}>
                  {p.product_code} — {p.product_name}
                </option>
              ))}
            </ThemedSelect>
          )}
        </div>
        <div>
          <label htmlFor="line-item-quantity" className={LABEL}>
            Quantity <span className="text-red-400">*</span>
          </label>
          <input
            id="line-item-quantity"
            type="number"
            min="0"
            step="0.01"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className={INPUT}
          />
        </div>
        <div>
          <label htmlFor="line-item-notes" className={LABEL}>
            Notes
          </label>
          <textarea
            id="line-item-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={INPUT}
          />
        </div>
        {error && <InlineAlert>{error}</InlineAlert>}
      </div>
      <ModalFooter submitLabel="Save" submitting={saving} onSubmit={submit} onCancel={onCancel} />
    </Modal>
  );
};

/* ─── Timeline Tab ────────────────────────── */
const TimelineTab = ({ timeline }: { timeline: OrderEvent[] }) => (
  <div className="bg-surface rounded-xl border border-slate-200 p-5">
    {timeline.length === 0 ? (
      <EmptyState
        icon={Clock}
        title="No timeline events"
        description="Timeline will appear as the order progresses"
      />
    ) : (
      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-200" />
        <div className="space-y-5">
          {timeline.map((event, i) => (
            <div
              key={event.event_id ?? i}
              className="relative pl-10 animate-slideUp"
              style={{ animationDelay: `${i * 50}ms` }}
            >
              <div className="absolute left-0 top-1 w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center border border-primary-200">
                <div className="w-2 h-2 bg-primary-500 rounded-full" />
              </div>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                <div className="flex items-start justify-between mb-1">
                  <h4 className="text-sm font-bold text-slate-800">{event.event_type}</h4>
                  <span className="text-[10px] text-slate-400 tabular-nums" style={MONO}>
                    {formatAppDateTime(event.event_date_time)}
                  </span>
                </div>
                <p className="text-xs text-slate-600">{event.event_description}</p>
                {event.event_user && (
                  <p className="text-[10px] text-slate-400 mt-1">By: {event.event_user}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

/* ─── History Tab ─────────────────────────── */
const HistoryTab = ({ history }: { history: OrderHistoryEntry[] }) => (
  <div className="bg-surface rounded-xl border border-slate-200">
    {history.length === 0 ? (
      <EmptyState
        icon={RefreshCw}
        title="No changes recorded"
        description="History will appear when the order is modified"
      />
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50/80">
              {['Date', 'User', 'Change Type', 'Description'].map((h) => (
                <th
                  key={h}
                  className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {history.map((c, i) => (
              <tr key={c.history_id ?? i} className="hover:bg-slate-50 transition-colors">
                <td className="px-5 py-3.5 text-xs text-slate-400 tabular-nums" style={MONO}>
                  {formatAppDateTime(c.history_date_time)}
                </td>
                <td className="px-5 py-3.5 text-xs text-slate-700 font-medium">{c.changed_by}</td>
                <td className="px-5 py-3.5 text-xs text-slate-800 font-semibold">
                  {c.change_type}
                </td>
                <td className="px-5 py-3.5 text-xs text-slate-600">
                  {c.change_description || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

/* ─── Events Tab ──────────────────────────── */
const EventsTab = ({ events }: { events: OrderEvent[] }) => (
  <div className="bg-surface rounded-xl border border-slate-200">
    {events.length === 0 ? (
      <EmptyState
        icon={AlertCircle}
        title="No events logged"
        description="Events will appear as actions are performed"
      />
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50/80">
              {['Timestamp', 'Event Type', 'Description', 'User'].map((h) => (
                <th
                  key={h}
                  className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {events.map((e, i) => (
              <tr key={e.event_id ?? i} className="hover:bg-slate-50 transition-colors">
                <td className="px-5 py-3.5 text-xs text-slate-400 tabular-nums" style={MONO}>
                  {formatAppDateTime(e.event_date_time)}
                </td>
                <td className="px-5 py-3.5">
                  <span className="px-2 py-0.5 text-[10px] font-semibold bg-primary-50 text-primary-700 rounded border border-primary-200">
                    {e.event_type}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-xs text-slate-600">{e.event_description}</td>
                <td className="px-5 py-3.5 text-xs text-slate-700 font-medium">
                  {e.event_user || 'System'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

/* ─── Status Update Modal (ORD-14) ────────── */
interface StatusUpdateModalProps {
  currentStatus: string;
  options: string[];
  onConfirm: (status: string, reason: string) => Promise<void>;
  onCancel: () => void;
}

const StatusUpdateModal = ({
  currentStatus,
  options,
  onConfirm,
  onCancel,
}: StatusUpdateModalProps) => {
  const [newStatus, setNewStatus] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm(newStatus, reason.trim());
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to update the status'));
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Update Status">
      {options.length === 0 ? (
        <p className="text-sm text-slate-600">
          A {currentStatus} order cannot change status any more.
        </p>
      ) : (
        <div className="space-y-4">
          <div>
            <span className={LABEL}>
              New Status <span className="text-red-400">*</span>
            </span>
            <ThemedSelect
              value={newStatus}
              onChange={(e) => setNewStatus(String(e.target.value))}
              aria-label="New Status"
              className={SELECT}
            >
              <option value="">Select Status</option>
              {options.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </ThemedSelect>
            <p className="text-[11px] text-slate-400 mt-1.5">
              Orders move Active → In Progress → Completed. On Hold pauses an order; Completed and
              Cancelled are final.
            </p>
          </div>
          <div>
            <label htmlFor="status-reason" className={LABEL}>
              Reason <span className="text-red-400">*</span>
            </label>
            <textarea
              id="status-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className={INPUT}
              placeholder="Why is the status changing..."
            />
          </div>
          {error && <InlineAlert>{error}</InlineAlert>}
        </div>
      )}
      <ModalFooter
        submitLabel="Update Status"
        submitting={submitting}
        disabled={!newStatus || !reason.trim()}
        onSubmit={submit}
        onCancel={onCancel}
      />
    </Modal>
  );
};

/* ─── Amendment Modal (ORD-2) ─────────────── */
interface AmendmentModalProps {
  order: OrderDetail;
  onConfirm: (data: OrderAmendmentData, reason: string) => Promise<void>;
  onCancel: () => void;
}

const AmendmentModal = ({ order, onConfirm, onCancel }: AmendmentModalProps) => {
  const [size, setSize] = useState<string>(
    order.order_size != null ? String(order.order_size) : ''
  );
  const [priority, setPriority] = useState<string>(order.order_priority || 'Normal');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const diff = buildAmendment(order, { order_size: size, order_priority: priority });
    if (!diff.ok) {
      setError(diff.error);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm(diff.data, reason.trim());
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to create the amendment'));
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Create Amendment" wide>
      <div className="space-y-4">
        <div>
          <label htmlFor="amend-size" className={LABEL}>
            Order Size (MT)
          </label>
          <input
            id="amend-size"
            type="number"
            min="0"
            step="0.001"
            value={size}
            onChange={(e) => setSize(e.target.value)}
            className={INPUT}
          />
        </div>
        <div>
          <span className={LABEL}>Priority</span>
          <ThemedSelect
            value={priority}
            onChange={(e) => setPriority(String(e.target.value))}
            aria-label="Priority"
            className={SELECT}
          >
            {['Low', 'Normal', 'High', 'Urgent'].map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <div>
          <label htmlFor="amend-reason" className={LABEL}>
            Reason <span className="text-red-400">*</span>
          </label>
          <textarea
            id="amend-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className={INPUT}
            placeholder="Why is this amendment needed..."
          />
        </div>
        {error && <InlineAlert>{error}</InlineAlert>}
      </div>
      <ModalFooter
        submitLabel="Create Amendment"
        submitting={submitting}
        disabled={!reason.trim()}
        onSubmit={submit}
        onCancel={onCancel}
      />
    </Modal>
  );
};

/** True when an `order:*` payload is about this order. */
const isAbout = (payload: unknown, orderNo: string | undefined): boolean =>
  !!orderNo && (payload as OrderSocketPayload | null | undefined)?.order_no === orderNo;

/* ═══════════════════════════════════════════════
   MAIN ORDER DETAILS COMPONENT
   ═══════════════════════════════════════════════ */
const OrderDetails = () => {
  const { orderNo } = useParams<{ orderNo: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(ORDERS_MANAGE);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [lineItems, setLineItems] = useState<OrderLineItem[]>([]);
  const [timeline, setTimeline] = useState<OrderEvent[]>([]);
  const [history, setHistory] = useState<OrderHistoryEntry[]>([]);
  const [events, setEvents] = useState<OrderEvent[]>([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showAmendmentModal, setShowAmendmentModal] = useState(false);
  // undefined = closed, null = Add, an item = Edit
  const [lineItemModal, setLineItemModal] = useState<OrderLineItem | null | undefined>(undefined);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchOrderDetails = useCallback(
    async (showSpinner = true) => {
      if (!orderNo) return;
      try {
        if (showSpinner) setLoading(true);
        const data = await orderService.getOrder(orderNo);
        // API wraps the order: { success, order } — store the inner order object
        setOrder(data.order ?? null);
      } catch (error) {
        console.error('Failed to fetch order details:', error);
      } finally {
        setLoading(false);
      }
    },
    [orderNo]
  );

  const fetchLineItems = useCallback(async () => {
    if (!orderNo) return;
    try {
      const data = await orderService.getLineItems(orderNo);
      setLineItems(data.line_items || []);
    } catch (error) {
      console.error('Failed to fetch line items:', error);
    }
  }, [orderNo]);

  const fetchAdditionalData = useCallback(async () => {
    if (!orderNo) return;
    try {
      const [historyData, eventsData] = await Promise.all([
        orderService.getOrderHistory(orderNo),
        orderService.getOrderEvents(orderNo),
      ]);
      const eventRows = eventsData.events || [];
      // Timeline is the event stream presented differently
      setTimeline(eventRows);
      setHistory(historyData.history || []);
      setEvents(eventRows);
    } catch (error) {
      console.error('Failed to fetch additional data:', error);
    }
  }, [orderNo]);

  useEffect(() => {
    fetchOrderDetails();
    fetchLineItems();
    fetchAdditionalData();
  }, [fetchOrderDetails, fetchLineItems, fetchAdditionalData]);

  const reloadOrder = () => {
    fetchOrderDetails(false);
    fetchAdditionalData();
  };

  // ORD-19: every order:* payload carries `order_no` at the top level.
  const refreshOrder = (payload: unknown) => {
    if (isAbout(payload, orderNo)) reloadOrder();
  };
  const refreshLineItems = (payload: unknown) => {
    if (isAbout(payload, orderNo)) fetchLineItems();
  };
  useOrderSubscription({
    onOrderUpdated: refreshOrder,
    onStatusChanged: refreshOrder,
    onOrderAmended: refreshOrder,
    onOrderCompleted: refreshOrder,
    onOrderCancelled: refreshOrder,
    onLineItemAdded: refreshLineItems,
    onLineItemUpdated: refreshLineItems,
    onLineItemDeleted: refreshLineItems,
  });

  // The modals catch a rejection and show it; they stay open until the call succeeds.
  const handleStatusUpdate = async (newStatus: string, reason: string) => {
    if (!orderNo) return;
    await orderService.updateOrderStatus(orderNo, newStatus, reason);
    setShowStatusModal(false);
    reloadOrder();
  };

  const handleCreateAmendment = async (data: OrderAmendmentData, reason: string) => {
    if (!orderNo) return;
    await orderService.createAmendment(orderNo, data, reason);
    setShowAmendmentModal(false);
    reloadOrder();
  };

  const handleCancelOrder = async () => {
    if (!orderNo) return;
    const reason = window.prompt('Reason for cancelling this order:');
    if (!reason) return;
    setActionError(null);
    try {
      await orderService.cancelOrder(orderNo, reason);
      reloadOrder();
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Failed to cancel the order'));
    }
  };

  const openLineItemModal = async (item: OrderLineItem | null) => {
    setLineItemModal(item);
    if (item === null && products.length === 0) {
      try {
        const data = await orderService.getProducts({ active: true });
        setProducts((data.products || []).filter((p) => p.active_status));
      } catch (err) {
        setActionError(apiErrorMessage(err, 'Failed to load the products'));
      }
    }
  };

  const handleSaveLineItem = async (data: LineItemInput) => {
    if (!orderNo) return;
    if (lineItemModal) {
      await orderService.updateLineItem(orderNo, lineItemModal.line_item_id, {
        quantity: data.quantity,
        notes: data.notes,
      });
    } else {
      await orderService.addLineItem(orderNo, data);
    }
    setLineItemModal(undefined);
    fetchLineItems();
  };

  const handleDeleteLineItem = async (item: OrderLineItem) => {
    if (!orderNo) return;
    if (!window.confirm(`Delete ${item.product_code} (${item.product_name}) from this order?`)) {
      return;
    }
    setActionError(null);
    try {
      await orderService.deleteLineItem(orderNo, item.line_item_id);
      fetchLineItems();
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Failed to delete the line item'));
    }
  };

  /* ─── Loading ─── */
  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-5">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-12 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  /* ─── Not Found ─── */
  if (!order) {
    return (
      <div className="p-6 lg:p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center max-w-md mx-auto">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-sm font-semibold text-red-800">Order not found</p>
        </div>
      </div>
    );
  }

  const canEditOrder = canManage && isOrderOpen(order.order_status);

  return (
    <div className="p-6 lg:p-8">
      <button
        onClick={() => navigate('/order-management/orders')}
        className="mb-5 flex items-center text-sm text-slate-500 hover:text-slate-800 font-medium transition-colors"
      >
        <ArrowLeft className="w-4 h-4 mr-1.5" />
        Back to Orders
      </button>

      {actionError && (
        <InlineAlert className="mb-4" onDismiss={() => setActionError(null)}>
          {actionError}
        </InlineAlert>
      )}

      <OrderHeader
        order={order}
        canManage={canManage}
        onStatusUpdate={() => setShowStatusModal(true)}
        onCreateAmendment={() => setShowAmendmentModal(true)}
        onCancel={handleCancelOrder}
      />

      <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'overview' && <OverviewTab order={order} />}
      {activeTab === 'lineItems' && (
        <LineItemsTab
          lineItems={lineItems}
          canEdit={canEditOrder}
          onRefresh={fetchLineItems}
          onAdd={() => openLineItemModal(null)}
          onEdit={(item) => openLineItemModal(item)}
          onDelete={handleDeleteLineItem}
        />
      )}
      {activeTab === 'timeline' && <TimelineTab timeline={timeline} />}
      {activeTab === 'history' && <HistoryTab history={history} />}
      {activeTab === 'events' && <EventsTab events={events} />}

      {showStatusModal && (
        <StatusUpdateModal
          currentStatus={order.order_status}
          options={allowedTransitions(order)}
          onConfirm={handleStatusUpdate}
          onCancel={() => setShowStatusModal(false)}
        />
      )}
      {showAmendmentModal && (
        <AmendmentModal
          order={order}
          onConfirm={handleCreateAmendment}
          onCancel={() => setShowAmendmentModal(false)}
        />
      )}
      {lineItemModal !== undefined && (
        <LineItemModal
          item={lineItemModal}
          products={products}
          onSave={handleSaveLineItem}
          onCancel={() => setLineItemModal(undefined)}
        />
      )}
    </div>
  );
};

export default OrderDetails;
